import type { CarePlan } from '@medplum/fhirtypes';

import {
  type CarePlanMatchCandidate,
  type CarePlanMatchRationale,
  type PlanShapeCode,
  carePlanShapeOf,
  decodeCarePlanMatch,
  diffDays,
} from '@eluve/plan-shapes';

export type CarePlanSummary = {
  title: string;
  // The plan shape stamped on the draft (ENG-4773). Undefined ⇒ phased (old
  // drafts predate the stamp). Phase fields below are meaningful only for
  // phased; verbatim (continuous/episodic) plans report 0 phases / null weeks.
  shape: PlanShapeCode | undefined;
  phaseCount: number;
  phaseTitles: string[];
  weeks: number | null;
  matchPct: number | null;
  rationale: CarePlanMatchRationale[];
  // The matcher's ranked pool (ENG-4764), winner included. Empty for pre-ENG-4764
  // drafts. The tab filters out the active plan to show the alternatives.
  candidates: CarePlanMatchCandidate[];
};

// The callout/recommendation-card data, derived from the draft CarePlan alone
// (no child fetch) so the polling callout stays cheap. Phases come off
// CarePlan.activity; the total span sums each dated phase's length.
export function carePlanSummary(carePlan: CarePlan): CarePlanSummary {
  const activities = carePlan.activity ?? [];
  const phaseTitles = activities.map(
    (a) =>
      a.detail?.code?.text ?? a.detail?.code?.coding?.[0]?.display ?? 'Phase',
  );
  const totalDays = activities.reduce((sum, a) => {
    const start = a.detail?.scheduledPeriod?.start;
    const end = a.detail?.scheduledPeriod?.end;
    return start && end ? sum + diffDays(start, end) : sum;
  }, 0);
  const match = decodeCarePlanMatch(carePlan);
  return {
    title: carePlan.title ?? 'Care plan',
    shape: carePlanShapeOf(carePlan),
    phaseCount: activities.length,
    phaseTitles,
    weeks: totalDays > 0 ? Math.round(totalDays / 7) : null,
    matchPct: match ? Math.round(match.confidence * 100) : null,
    rationale: match?.rationale ?? [],
    candidates: match?.candidates ?? [],
  };
}
